import { useId, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { muscleLabel } from "./lib/format";
import {
  LOW_READINESS,
  SECONDARY_READY_MIN,
  barGeometry,
  isQuiet,
  lastTrainedWords,
  muscleGroups,
  readinessWords,
  weekWords,
} from "./lib/progress";

const pct = (fraction) => `${Math.round(fraction * 100)}%`;

/**
 * One muscle's week. Plain text first — "Chest: 6 of 9 sets this week",
 * then how recovered it is and when it was last trained — and the bar
 * after it, which only draws what the text already says (aria-hidden):
 * the sets done, with a tick at the weekly target — and no bar without a
 * target, where it would have nothing to measure. Under it the target's
 * reason, verbatim: always shown from md up, behind a "Why" toggle on a
 * phone, where nineteen of them would bury the bars.
 */
function MuscleRow({ muscle, timeZone, now }) {
  const id = useId();
  const [why, setWhy] = useState(false);
  const label = muscle.label || muscleLabel(muscle.muscle);
  const quiet = isQuiet(muscle);
  const bar = barGeometry(muscle.volume, muscle.target);
  const readiness = readinessWords(muscle.readiness);
  const trained = lastTrainedWords(muscle.last_trained_at, timeZone, now);
  const reason = typeof muscle.reason === "string" ? muscle.reason.trim() : "";
  return (
    <li className={cn("flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0", quiet && "text-muted-foreground")}>
      <p className="m-0 flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="font-medium">
          {label}
          <span className="sr-only">:</span>
        </span>{" "}
        <span className="text-sm tabular-nums">{weekWords(muscle.volume, muscle.target)}</span>
      </p>
      {bar !== null && (
        // the track in moss at 20%: --muted was 1.09:1 on a dark card
        <div aria-hidden="true" data-bar className="relative my-1 h-2 rounded-full bg-primary/20">
          <div className="h-full rounded-full bg-primary" style={{ width: `${bar.fill}%` }} />
          <div
            data-target-tick
            className="absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded-full bg-foreground"
            style={{ left: `${bar.tick}%` }}
          />
        </div>
      )}
      {/* The phone's Why toggle shares this line (a row of its own made
          nineteen rows ~50 px taller each); from md up the reason is
          always shown and the toggle is gone. */}
      <div className="flex items-center gap-2">
        <p className="m-0 flex-1 text-sm text-muted-foreground">
          {readiness !== null && (
            <>
              {readiness}
              {" · "}
            </>
          )}
          {trained}
        </p>
        {reason !== "" && (
          <>
            {" "}
            <Button
              type="button"
              variant="ghost"
              className="-my-2 -mr-3 h-11 shrink-0 px-3 text-primary md:hidden"
              aria-expanded={why}
              aria-controls={`${id}-why`}
              onClick={() => setWhy(!why)}
            >
              Why{" "}
              <span className="sr-only">
                {Number(muscle.target) > 0 ? "this target" : "no target"} for {label}
              </span>
              <ChevronDown aria-hidden="true" className={cn("transition-transform", why && "rotate-180")} />
            </Button>
          </>
        )}
      </div>
      {reason !== "" && (
        <p id={`${id}-why`} className={cn("m-0 text-sm text-muted-foreground", why ? "block" : "hidden md:block")}>
          {reason}
        </p>
      )}
    </li>
  );
}

MuscleRow.propTypes = {
  /** A TrainingMuscleSummary. */
  muscle: PropTypes.shape({
    muscle: PropTypes.string.isRequired,
    label: PropTypes.string,
    readiness: PropTypes.number,
    volume: PropTypes.number,
    target: PropTypes.number,
    last_trained_at: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
  timeZone: PropTypes.string,
  now: PropTypes.instanceOf(Date).isRequired,
};

/**
 * Progress's "This week" (spec §6: weekly sets per muscle vs target): every
 * muscle from summary/, grouped by the check-in's regions in the
 * catalog's order. Readiness is said in words from the engine's own
 * thresholds (lib/progress.js), never as a bare percentage.
 */
function MuscleWeek({ muscles, timeZone, now, headingRef }) {
  const id = useId();
  const groups = muscleGroups(muscles);
  return (
    <section aria-labelledby={`${id}-title`} className="flex scroll-mt-24 flex-col gap-3">
      <div>
        <h2
          id={`${id}-title`}
          ref={headingRef}
          tabIndex={-1}
          className="m-0 font-display text-xl font-semibold focus-visible:outline-none"
        >
          This week
        </h2>
        <p className="mt-1 mb-0 text-sm text-muted-foreground">
          Sets in the last 7 days against each muscle&apos;s weekly target. A set counts whole for the muscle an
          exercise works directly, and half for one it helps. The tick on each bar is the target. Last trained
          counts a session that gave the muscle at least one whole set.
        </p>
        <p className="mt-1 mb-0 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Recovered</span>: the plan treats it as ready.{" "}
          <span className="font-medium text-foreground">Recovering</span> (under {pct(LOW_READINESS)}): a plan that
          leans on it scores lower today. <span className="font-medium text-foreground">Tired</span> (under{" "}
          {pct(SECONDARY_READY_MIN)}): exercises that work it on the side are left out too.
        </p>
      </div>
      {groups.map((group) => (
        <section
          key={group.region}
          aria-labelledby={`${id}-${group.region}`}
          className="rounded-lg border bg-card p-4"
        >
          <h3
            id={`${id}-${group.region}`}
            className="mt-0 mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
          >
            {group.label}
          </h3>
          <ul aria-labelledby={`${id}-${group.region}`} className="m-0 list-none divide-y p-0">
            {group.muscles.map((muscle) => (
              <MuscleRow key={muscle.muscle} muscle={muscle} timeZone={timeZone} now={now} />
            ))}
          </ul>
        </section>
      ))}
    </section>
  );
}

MuscleWeek.propTypes = {
  /** summary/'s muscles. */
  muscles: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The settings' time zone: "last trained" counts calendar days there. */
  timeZone: PropTypes.string,
  now: PropTypes.instanceOf(Date).isRequired,
  /** Where focus lands after a Retry brings the page in. */
  headingRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default MuscleWeek;
